import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import type { ContainerType } from 'src/trpc';
import { safeMoneyFromCents } from '@libs/services/money';

export interface MatrixCombination {
  enabled: boolean;
  fixedPriceInCents?: number;
  includedDays?: number;
  dailyOverageFeeInCents?: number;
}

export type MatrixData = Record<string, Record<string, MatrixCombination>>;

export interface ApiMaterial {
  _id: string;
  name: string;
  [key: string]: unknown;
}

interface ContainerMaterialMatrixProps {
  matrixData: Record<string, Record<string, MatrixCombination>>;
  onMatrixChange: (data: Record<string, Record<string, MatrixCombination>>) => void;
  containers: ContainerType[];
  materialsData: ApiMaterial[];
}

export function ContainerMaterialMatrix({
  matrixData,
  onMatrixChange,
  containers,
  materialsData,
}: ContainerMaterialMatrixProps) {
  const [selectedMaterials, setSelectedMaterials] = useState(Object.keys(matrixData));

  const addMaterial = (materialId: string) => {
    if (!selectedMaterials.includes(materialId)) {
      const newSelectedMaterials = [...selectedMaterials, materialId];
      setSelectedMaterials(newSelectedMaterials);

      const newMatrixData = { ...matrixData };
      newMatrixData[materialId] = {};
      containers.forEach((container) => {
        if (container._id) {
          newMatrixData[materialId][container._id] = {
            enabled: false,
            fixedPriceInCents: 0,
            includedDays: 1,
            dailyOverageFeeInCents: 0,
          };
        }
      });
      onMatrixChange(newMatrixData);
    }
  };

  const removeMaterial = (materialId: string) => {
    const newSelectedMaterials = selectedMaterials.filter((id) => id !== materialId);
    setSelectedMaterials(newSelectedMaterials);

    const newMatrixData = { ...matrixData };
    delete newMatrixData[materialId];
    onMatrixChange(newMatrixData);
  };

  const updateCombination = (
    materialId: string,
    containerId: string,
    field: keyof MatrixCombination,
    value: boolean | number | 'indeterminate',
  ) => {
    const newMatrixData = { ...matrixData };
    if (!newMatrixData[materialId]) {
      newMatrixData[materialId] = {};
    }
    if (!newMatrixData[materialId][containerId]) {
      newMatrixData[materialId][containerId] = { enabled: false };
    }

    // TODO: We can do this in a much more type safe way by using a union type for the field and value
    if (field === 'enabled' && typeof value === 'boolean') {
      newMatrixData[materialId][containerId].enabled = value;
    } else if (field === 'fixedPriceInCents' && typeof value === 'number') {
      newMatrixData[materialId][containerId].fixedPriceInCents = value;
    } else if (field === 'includedDays' && typeof value === 'number') {
      newMatrixData[materialId][containerId].includedDays = value;
    } else if (field === 'dailyOverageFeeInCents' && typeof value === 'number') {
      newMatrixData[materialId][containerId].dailyOverageFeeInCents = value;
    }

    onMatrixChange(newMatrixData);
  };

  // TODO: Should we not use money service here?

  return (
    <div className="space-y-4">
      {/* Material Selection */}
      <div>
        <Label>Materials</Label>
        <Select onValueChange={addMaterial}>
          <SelectTrigger>
            <SelectValue placeholder="Add material to matrix" />
          </SelectTrigger>
          <SelectContent>
            {materialsData
              .filter((material) => !selectedMaterials.includes(material._id))
              .map((material) => (
                <SelectItem key={material._id} value={material._id}>
                  {material.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {selectedMaterials.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedMaterials.map((materialId) => {
              const material = materialsData.find((m) => m._id === materialId);
              return (
                <Badge key={materialId} variant="secondary">
                  {material?.name}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-1 h-auto p-0 hover:bg-transparent"
                    onClick={() => removeMaterial(materialId)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      {/* Matrix Configuration */}
      {selectedMaterials.length > 0 && (
        <div className="space-y-6">
          {selectedMaterials.map((materialId) => {
            const material = materialsData.find((m) => m._id === materialId);
            const materialData = matrixData[materialId] || {};

            return (
              <Card key={materialId}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    {material?.name} Pricing
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMaterial(materialId)}
                      className="text-red-500 hover:text-red-500 hover:bg-red-500/10 dark:text-red-900 dark:hover:text-red-900 dark:hover:bg-red-900/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Container</TableHead>
                          <TableHead className="text-center">Enable</TableHead>
                          <TableHead>Fixed Price</TableHead>
                          <TableHead>Included Days</TableHead>
                          <TableHead>Daily Overage Fee</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {containers
                          .filter((container) => container._id)
                          .map((container) => {
                            const containerId = container._id!;
                            const combinationData = materialData[containerId] || { enabled: false };

                            return (
                              <TableRow key={containerId}>
                                <TableCell className="font-medium">{container.name}</TableCell>
                                <TableCell className="text-center">
                                  <Checkbox
                                    checked={combinationData.enabled || false}
                                    onCheckedChange={(checked) => {
                                      updateCombination(
                                        materialId,
                                        containerId,
                                        'enabled',
                                        checked as boolean,
                                      );
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    placeholder="$0.00"
                                    value={
                                      combinationData.fixedPriceInCents
                                        ? (combinationData.fixedPriceInCents / 100).toString()
                                        : ''
                                    }
                                    onChange={(e) => {
                                      const dollarValue = parseFloat(e.target.value) || 0;
                                      const centsValue = Math.round(dollarValue * 100);
                                      updateCombination(
                                        materialId,
                                        containerId,
                                        'fixedPriceInCents',
                                        centsValue,
                                      );
                                    }}
                                    disabled={!combinationData.enabled}
                                    className={
                                      combinationData.enabled &&
                                      (!combinationData.fixedPriceInCents ||
                                        combinationData.fixedPriceInCents <= 0)
                                        ? 'border-red-500 focus:border-red-500'
                                        : ''
                                    }
                                    required={combinationData.enabled}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    placeholder="Days"
                                    min="1"
                                    value={combinationData.includedDays || ''}
                                    onChange={(e) =>
                                      updateCombination(
                                        materialId,
                                        containerId,
                                        'includedDays',
                                        parseInt(e.target.value) || 0,
                                      )
                                    }
                                    disabled={!combinationData.enabled}
                                    className={
                                      combinationData.enabled &&
                                      (!combinationData.includedDays ||
                                        combinationData.includedDays <= 0)
                                        ? 'border-red-500 focus:border-red-500'
                                        : ''
                                    }
                                    required={combinationData.enabled}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    placeholder="$0.00"
                                    value={
                                      combinationData.dailyOverageFeeInCents
                                        ? (combinationData.dailyOverageFeeInCents / 100).toString()
                                        : ''
                                    }
                                    onChange={(e) => {
                                      const dollarValue = parseFloat(e.target.value) || 0;
                                      const centsValue = Math.round(dollarValue * 100);
                                      updateCombination(
                                        materialId,
                                        containerId,
                                        'dailyOverageFeeInCents',
                                        centsValue,
                                      );
                                    }}
                                    disabled={!combinationData.enabled}
                                    className={
                                      combinationData.enabled &&
                                      combinationData.dailyOverageFeeInCents === undefined
                                        ? 'border-red-500 focus:border-red-500'
                                        : ''
                                    }
                                    required={combinationData.enabled}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Summary */}
                  <div className="mt-4 p-3 bg-slate-100 rounded-lg dark:bg-slate-800">
                    <div className="text-sm font-medium mb-2">
                      Example pricing on selected dumpsters:
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {Object.entries(materialData)
                        .filter(([_, data]) => data.enabled)
                        .map(([containerId, data]) => {
                          const container = containers.find((c) => c._id === containerId);
                          const text = `${container?.name}: ${safeMoneyFromCents(data.fixedPriceInCents ? data.fixedPriceInCents : 0).toFormat()} (${data.includedDays || 0} days, ${safeMoneyFromCents(data.dailyOverageFeeInCents ? data.dailyOverageFeeInCents : 0).toFormat()}/day overage)`;
                          return <p key={containerId}>{text}</p>;
                        }) || 'No combinations enabled'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
